import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../../api/client';
import StatusBadge from '../common/StatusBadge';
import ConfirmDialog from '../common/ConfirmDialog';

const ROTATION_TYPES = [
  { value: 'sequential', label: 'Secuencial' },
  { value: 'random', label: 'Aleatorio' },
  { value: 'weighted', label: 'Ponderado' },
];

export default function RotationEditor({ editMode = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = editMode && Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    rotation_type: 'weighted',
    is_active: true,
    description: '',
    allow_duplicates: false,
    repeat_mode: 'smart',
  });

  const [tracks, setTracks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addingTrack, setAddingTrack] = useState(false);
  const [currentCycle, setCurrentCycle] = useState([]);
  const [errors, setErrors] = useState({});

  const REPEAT_MODES = [
    { value: 'smart', label: 'Inteligente (aleatorio)' },
    { value: 'strict', label: 'Estricto (secuencial)' },
  ];

  useEffect(() => {
    if (isEdit) {
      fetchRotation();
      fetchTracks();
      fetchCycle();
    }
  }, [id]);

  const fetchRotation = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/rotations/${id}`);
      setFormData({
        name: data.name || '',
        rotation_type: data.rotation_type || 'weighted',
        is_active: data.is_active ?? true,
        description: data.description || '',
        allow_duplicates: data.allow_duplicates ?? false,
        repeat_mode: data.repeat_mode || 'smart',
      });
    } catch (err) {
      console.error('[RotationEditor] Fetch failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTracks = async () => {
    try {
      const { data } = await api.get(`/api/rotations/${id}/tracks`);
      setTracks(data);
    } catch (err) {
      console.error('[RotationEditor] Failed to fetch tracks:', err);
    }
  };

  const fetchCycle = async () => {
    try {
      const { data } = await api.get(`/api/rotations/${id}/cycle`);
      setCurrentCycle(data || []);
    } catch (err) {
      console.error('[RotationEditor] Failed to fetch cycle:', err);
    }
  };

  const searchMedia = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const { data } = await api.get('/api/media', {
        params: { search: term, limit: 20, processing_status: 'ready' },
      });
      const files = data.files || [];
      setSearchResults(files.map((track) => ({
        id: track.id,
        media_file_id: track.id,
        title: track.metadata?.title || track.original_filename,
        artist: track.metadata?.artist || '',
        file_path: track.stored_filename,
        duration_seconds: track.duration_seconds,
        weight: 50,
      })));
    } catch (err) {
      console.error('[RotationEditor] Search failed:', err);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchMedia(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm, searchMedia]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleAddTrack = async (track) => {
    if (!isEdit) return;
    setAddingTrack(true);
    try {
      const { data } = await api.post(`/api/rotations/${id}/tracks`, {
        media_file_id: track.id,
        weight: 50,
      });
      setTracks([...tracks, data]);
      setSearchResults([]);
      setSearchTerm('');
    } catch (err) {
      console.error('[RotationEditor] Add track failed:', err);
    } finally {
      setAddingTrack(false);
    }
  };

  const handleWeightChange = async (trackId, weight) => {
    const updated = tracks.map((t) =>
      t.id === trackId ? { ...t, weight: parseInt(weight, 10) || 0 } : t
    );
    setTracks(updated);
    await api.put(`/api/rotations/${id}/tracks/${trackId}`, { weight: parseInt(weight, 10) || 0 });
  };

  const handleRemoveTrack = async (trackId) => {
    try {
      await api.delete(`/api/rotations/${id}/tracks/${trackId}`);
      setTracks(tracks.filter((t) => t.id !== trackId));
    } catch (err) {
      console.error('[RotationEditor] Remove track failed:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setErrors({ name: 'El nombre de la rotación es obligatorio' });
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/api/rotations/${id}`, formData);
        navigate('/rotations');
      } else {
        const { data } = await api.post('/api/rotations', formData);
        navigate(`/rotations/${data.id}`);
      }
    } catch (err) {
      console.error('[RotationEditor] Save failed:', err);
      setErrors({ submit: 'Error al guardar la rotación.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-dark-400">Cargando rotación...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">
            {isEdit ? 'Editar Rotación' : 'Crear Rotación'}
          </h1>
        </div>
        <Link to="/rotations" className="btn btn-secondary btn-sm">
          Volver a Rotaciones
        </Link>
      </div>

      {errors.submit && (
        <div className="rounded-lg bg-error-900/30 border border-error-700 p-3 text-sm text-error-400">
          {errors.submit}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="rounded-xl bg-dark-850 border border-dark-700 p-6">
          <h3 className="text-lg font-semibold text-dark-100 mb-4">Ajustes de Rotación</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-dark-300 mb-1">Nombre *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`input ${errors.name ? 'border-error-500' : ''}`}
                placeholder="Nombre de la rotación"
              />
              {errors.name && <p className="text-xs text-error-400 mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-300 mb-1">Tipo</label>
              <select name="rotation_type" value={formData.rotation_type} onChange={handleChange} className="input">
                {ROTATION_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-dark-300 mb-1">Descripción</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="input min-h-[60px]"
              placeholder="Descripción opcional..."
            />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex items-center gap-3 pt-2">
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="h-4 w-4 rounded border-dark-700 text-primary-600 focus:ring-primary-600"
              />
              <label htmlFor="is_active" className="text-sm text-dark-300">Activo</label>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <input
                type="checkbox"
                id="allow_duplicates"
                name="allow_duplicates"
                checked={formData.allow_duplicates}
                onChange={handleChange}
                className="h-4 w-4 rounded border-dark-700 text-primary-600 focus:ring-primary-600"
              />
              <label htmlFor="allow_duplicates" className="text-sm text-dark-300">Permitir duplicados</label>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-dark-300 mb-1">Modo de Repetición</label>
            <select name="repeat_mode" value={formData.repeat_mode} onChange={handleChange} className="input w-48">
              {REPEAT_MODES.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
        </div>

        {isEdit && (
          <div className="rounded-xl bg-dark-850 border border-dark-700 p-6">
            <h3 className="text-lg font-semibold text-dark-100 mb-4">Buscar Tracks</h3>
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar medios para añadir..."
                className="input pr-10"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-500">🔍</span>
            </div>

            {searchLoading && <p className="text-sm text-dark-400 mt-2">Buscando...</p>}

            {searchResults.length > 0 && (
              <div className="mt-3 max-h-60 overflow-y-auto rounded-lg border border-dark-700">
                {searchResults.map((track) => (
                  <div
                    key={track.id}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-dark-800 cursor-pointer"
                    onClick={() => handleAddTrack(track)}
                  >
                    <span className="text-xl">🎵</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-dark-100">{track.title}</p>
                      <p className="text-xs text-dark-400">{track.artist}</p>
                    </div>
                    {addingTrack && (
                      <span className="text-xs text-dark-500">Añadiendo...</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {isEdit && (
          <div className="rounded-xl bg-dark-850 border border-dark-700 p-6">
            <h3 className="text-lg font-semibold text-dark-100 mb-4">Tracks ({tracks.length})</h3>
            {tracks.length === 0 ? (
              <p className="text-sm text-dark-500">No hay tracks añadidos. Busca medios arriba para añadir tracks.</p>
            ) : (
              <div className="space-y-2">
                {tracks.map((track) => (
                  <div key={track.id} className="flex items-center gap-3 rounded-lg bg-dark-800/50 px-3 py-2">
                    <span className="text-lg">🎵</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-dark-100 truncate">{track.title}</p>
                      <p className="text-xs text-dark-400 truncate">{track.artist}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={track.weight ?? 50}
                        onChange={(e) => handleWeightChange(track.id, e.target.value)}
                        className="input text-xs w-16 text-center"
                        title="Peso (1-100)"
                      />
                      <span className="text-xs text-dark-500">%
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveTrack(track.id)}
                        className="text-xs text-error-400 hover:text-error-300"
                        title="Eliminar de la rotación"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {isEdit && currentCycle.length > 0 && (
          <div className="rounded-xl bg-dark-850 border border-dark-700 p-6">
            <h3 className="text-lg font-semibold text-dark-100 mb-4">Orden del Ciclo Actual</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {currentCycle.map((entry, idx) => (
                <div key={entry.id || idx} className="flex items-center gap-3 text-sm">
                  <span className="text-xs font-mono text-dark-500 w-6">{idx + 1}</span>
                  <span className="text-dark-100">{entry.title || entry.artist || 'Desconocido'}</span>
                  {entry.weight && (
                    <span className="text-xs text-dark-500 ml-auto">peso: {entry.weight}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-dark-700">
          <Link to="/rotations" className="btn btn-secondary">
            Cancelar
          </Link>
          <button type="submit" disabled={saving} className="btn btn-primary">
            {saving ? 'Guardando...' : isEdit ? 'Actualizar Rotación' : 'Crear Rotación'}
          </button>
        </div>
      </form>
    </div>
  );
}
